/**
 * Project JSON Schema and Sample Projects
 *
 * This defines the schema for Taurus Project Mode JSONs and provides
 * sample projects for users to get started with.
 */

// JSON Schema for Monaco editor validation and autocomplete
export const PROJECT_JSON_SCHEMA = {
  $schema: "http://json-schema.org/draft-07/schema#",
  type: "object",
  required: ["project_name", "steps"],
  properties: {
    project_name: {
      type: "string",
      description: "Display name for the project"
    },
    description: {
      type: "string",
      description: "Optional description of what this project does"
    },
    start_from: {
      type: "integer",
      minimum: 1,
      description: "Skip steps before this number (1-indexed). E.g., start_from: 5 skips steps 1-4."
    },
    start_from_id: {
      type: "string",
      description: "Skip steps before the step with this ID. Alternative to start_from."
    },
    defaults: {
      type: "object",
      description: "Default settings applied to all steps unless overridden",
      properties: {
        context_mode: {
          type: "string",
          enum: ["reset", "continue", "summary"],
          default: "continue",
          description: "reset: fresh context. continue: accumulate context. summary: fresh + inject progress summary."
        },
        review_mode: {
          type: "string",
          enum: ["auto", "user", "none"],
          default: "user",
          description: "auto: AI self-reviews and continues. user: pause for your approval. none: continue immediately."
        },
        datasource: {
          type: "string",
          description: "Default database connection name for SQL queries"
        },
        output_dir: {
          type: "string",
          description: "Default directory for saving artifacts"
        },
        delay_ms: {
          type: "number",
          default: 0,
          description: "Milliseconds to wait before starting each step (rate limiting)"
        }
      }
    },
    steps: {
      type: "array",
      minItems: 1,
      description: "List of steps to execute",
      items: {
        type: "object",
        required: ["name", "prompt"],
        properties: {
          id: {
            type: "string",
            description: "Unique identifier (auto-generated if not provided)"
          },
          name: {
            type: "string",
            description: "Display name for this step"
          },
          prompt: {
            type: "string",
            description: "Instructions for what the AI should do in this step"
          },
          skip: {
            type: "boolean",
            default: false,
            description: "Skip this step during execution"
          },
          context_mode: {
            type: "string",
            enum: ["reset", "continue", "summary"],
            description: "Override default context handling for this step"
          },
          review_mode: {
            type: "string",
            enum: ["auto", "user", "none"],
            description: "Override default review mode for this step"
          },
          datasource: {
            type: "string",
            description: "Database connection name for this step"
          },
          reference_files: {
            type: "array",
            items: { type: "string" },
            description: "Files to read/analyze in this step"
          },
          output_dir: {
            type: "string",
            description: "Directory to save artifacts from this step"
          },
          delay_ms: {
            type: "number",
            description: "Wait time before starting this step"
          },
          success_criteria: {
            type: "string",
            description: "How to evaluate if this step succeeded"
          },
          review_notes: {
            type: "string",
            description: "Guidance for auto-review: what to verify, check, or flag during self-review"
          }
        }
      }
    }
  }
};

// Sample Sequential Project - demonstrates context continuation
export const SAMPLE_SEQUENTIAL_PROJECT = {
  project_name: "Data Analysis Pipeline",
  description: "Load data, check quality, analyze trends, and generate a report",

  defaults: {
    context_mode: "continue",
    review_mode: "user"
  },

  steps: [
    {
      id: "load",
      name: "Load Data",
      prompt: "Load the CSV file and create a summary of the data including row count, column names, and data types. Identify any obvious data quality issues.",
      review_mode: "auto",
      success_criteria: "Data loaded successfully with summary displayed",
      review_notes: "Verify the dataframe loaded without errors. Check that column count and row count are reasonable. Flag any parsing warnings."
    },
    {
      id: "quality",
      name: "Quality Check",
      prompt: "Perform data quality analysis: check for null values, duplicates, outliers, and invalid entries. Calculate a quality score and recommend whether to proceed.",
      review_mode: "user",
      success_criteria: "Quality score above 90%"
    },
    {
      id: "analysis",
      name: "Trend Analysis",
      prompt: "Analyze trends and patterns in the data. Create visualizations showing key insights. Identify the top 5 most important findings.",
      review_mode: "auto",
      review_notes: "Confirm all charts saved successfully. Verify trends are clearly labeled. Check that insights are data-driven, not speculative."
    },
    {
      id: "report",
      name: "Generate Report",
      prompt: "Create an executive summary report with: key findings, data quality notes, trend visualizations, and recommendations for next steps.",
      review_mode: "user",
      output_dir: "./outputs/report"
    }
  ]
};

// Sample Batch Project - demonstrates independent steps with fresh context
export const SAMPLE_BATCH_PROJECT = {
  project_name: "Multi-Source Data Audit",
  description: "Run independent quality checks on multiple data sources",

  defaults: {
    context_mode: "reset",
    review_mode: "auto",
    delay_ms: 1000
  },

  steps: [
    {
      id: "audit_sales",
      name: "Audit Sales Data",
      prompt: "Load sales.csv and perform a comprehensive data quality audit. Check for missing values, invalid dates, negative amounts, and duplicate transaction IDs. Generate a quality report.",
      reference_files: ["sales.csv"],
      output_dir: "./audit/sales",
      review_notes: "Verify report file was created. Check that all quality metrics are present. Flag if error rate exceeds 5%."
    },
    {
      id: "audit_customers",
      name: "Audit Customer Data",
      prompt: "Load customers.csv and audit for data quality issues. Validate email formats, check for duplicate customer IDs, verify phone number formats, and flag incomplete records.",
      reference_files: ["customers.csv"],
      output_dir: "./audit/customers",
      review_notes: "Confirm email validation ran. Verify duplicate check completed. Ensure phone format issues are logged."
    },
    {
      id: "audit_inventory",
      name: "Audit Inventory Data",
      prompt: "Load inventory.csv and check for data integrity. Identify negative stock levels, missing product codes, price anomalies, and category inconsistencies.",
      reference_files: ["inventory.csv"],
      output_dir: "./audit/inventory",
      review_notes: "Check for negative stock alerts. Verify price outliers are flagged. Confirm category mapping is complete."
    },
    {
      id: "summary",
      name: "Consolidate Results",
      prompt: "Review all audit outputs and create a consolidated summary showing overall data health across all sources, prioritized issues to fix, and recommended actions.",
      review_mode: "user",
      output_dir: "./audit/summary"
    }
  ]
};

// Sample Quick Demo - simple 2-step project to test features
export const SAMPLE_QUICK_DEMO = {
  project_name: "Quick Demo",
  description: "Simple 2-step demo to verify project execution",

  defaults: {
    context_mode: "continue",
    review_mode: "none"
  },

  steps: [
    {
      id: "calculate",
      name: "Calculate",
      prompt: "Calculate 10 + 20 + 30 and store the result in a variable called 'total'. Print the result.",
      success_criteria: "Should print 60"
    },
    {
      id: "verify",
      name: "Verify",
      prompt: "Multiply 'total' by 2 and print the result to verify context continuation works.",
      success_criteria: "Should print 120"
    }
  ]
};

// Sample Database Analysis - demonstrates datasource usage
export const SAMPLE_DB_ANALYSIS = {
  project_name: "Database Health Check",
  description: "Analyze database tables, check data quality, generate insights",

  defaults: {
    context_mode: "continue",
    review_mode: "user"
  },

  steps: [
    {
      id: "explore",
      name: "Explore Schema",
      prompt: "List all tables in the database and show the row count for each. Identify the largest tables.",
      review_mode: "auto",
      review_notes: "Verify all tables were listed. Check that row counts are reasonable (no zeros for expected data). Note any permission errors."
    },
    {
      id: "quality",
      name: "Data Quality",
      prompt: "For the top 3 largest tables, check for: NULL values in key columns, duplicate records, and data freshness (most recent timestamps).",
      review_mode: "auto",
      success_criteria: "NULL rate below 5%, no critical duplicates found",
      review_notes: "Verify NULL analysis completed for all key columns. Check duplicate detection ran successfully. Confirm timestamps are recent."
    },
    {
      id: "insights",
      name: "Generate Insights",
      prompt: "Based on the data exploration, provide 3-5 actionable insights about the data quality and potential issues to investigate.",
      review_mode: "user"
    }
  ]
};

// Empty template for new projects
export const EMPTY_PROJECT_TEMPLATE = {
  project_name: "New Project",
  description: "Describe what this project does",

  defaults: {
    context_mode: "continue",
    review_mode: "user"
  },

  steps: [
    {
      id: "step1",
      name: "Step 1",
      prompt: "Describe what this step should do..."
    },
    {
      id: "step2",
      name: "Step 2",
      prompt: "Describe the next step..."
    }
  ]
};

// All sample projects with feature tags
export const SAMPLE_PROJECTS = [
  {
    id: 'sample_quick_demo',
    name: 'Quick Demo',
    description: '2 steps - test context continuation',
    features: ['context: continue', 'review: none'],
    isSample: true,
    json: SAMPLE_QUICK_DEMO
  },
  {
    id: 'sample_sequential',
    name: 'Data Analysis Pipeline',
    description: '4 steps - load, analyze, report',
    features: ['context: continue', 'review: auto', 'review_notes', 'success_criteria'],
    isSample: true,
    json: SAMPLE_SEQUENTIAL_PROJECT
  },
  {
    id: 'sample_batch',
    name: 'Multi-Source Audit',
    description: '4 independent checks with fresh context',
    features: ['context: reset', 'review_notes', 'delay_ms'],
    isSample: true,
    json: SAMPLE_BATCH_PROJECT
  },
  {
    id: 'sample_db_analysis',
    name: 'Database Health Check',
    description: '3 steps - explore schema, check quality',
    features: ['context: continue', 'review: auto', 'review_notes', 'datasource'],
    isSample: true,
    json: SAMPLE_DB_ANALYSIS
  }
];

// Helper to format JSON for display
export const formatProjectJson = (json) => {
  return JSON.stringify(json, null, 2);
};

// Helper to validate project JSON
export const validateProjectJson = (jsonString) => {
  try {
    const parsed = JSON.parse(jsonString);

    const errors = [];

    // Check required fields
    if (!parsed.project_name || typeof parsed.project_name !== 'string') {
      errors.push('project_name is required and must be a string');
    }

    if (!parsed.steps || !Array.isArray(parsed.steps) || parsed.steps.length === 0) {
      errors.push('steps is required and must be a non-empty array');
    } else {
      // Validate each step
      parsed.steps.forEach((step, index) => {
        if (!step.name || typeof step.name !== 'string') {
          errors.push(`Step ${index + 1}: name is required`);
        }
        if (!step.prompt || typeof step.prompt !== 'string') {
          errors.push(`Step ${index + 1}: prompt is required`);
        }
        if (step.review_mode && !['auto', 'user', 'none'].includes(step.review_mode)) {
          errors.push(`Step ${index + 1}: review_mode must be auto, user, or none`);
        }
        if (step.context_mode && !['reset', 'continue', 'summary'].includes(step.context_mode)) {
          errors.push(`Step ${index + 1}: context_mode must be reset, continue, or summary`);
        }
      });
    }

    // Validate defaults if provided
    if (parsed.defaults) {
      if (parsed.defaults.review_mode && !['auto', 'user', 'none'].includes(parsed.defaults.review_mode)) {
        errors.push('defaults.review_mode must be auto, user, or none');
      }
      if (parsed.defaults.context_mode && !['reset', 'continue', 'summary'].includes(parsed.defaults.context_mode)) {
        errors.push('defaults.context_mode must be reset, continue, or summary');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      parsed: errors.length === 0 ? parsed : null,
      stepCount: parsed.steps?.length || 0
    };
  } catch (e) {
    return {
      valid: false,
      errors: [`Invalid JSON: ${e.message}`],
      parsed: null,
      stepCount: 0
    };
  }
};

// Helper to extract step summary from parsed JSON
export const extractStepSummary = (parsed) => {
  if (!parsed || !parsed.steps) return [];

  // Determine start_from index (0-based internally)
  let startFromIndex = 0;
  if (parsed.start_from && parsed.start_from > 1) {
    startFromIndex = parsed.start_from - 1; // Convert to 0-based
  } else if (parsed.start_from_id) {
    const foundIndex = parsed.steps.findIndex(s => s.id === parsed.start_from_id);
    if (foundIndex > 0) startFromIndex = foundIndex;
  }

  return parsed.steps.map((step, index) => ({
    index: index + 1,
    id: step.id || `step_${index + 1}`,
    name: step.name || `Step ${index + 1}`,
    reviewMode: step.review_mode || parsed.defaults?.review_mode || 'user',
    contextMode: step.context_mode || parsed.defaults?.context_mode || 'continue',
    hasDatasource: !!step.datasource,
    hasFiles: !!(step.reference_files && step.reference_files.length > 0),
    hasOutput: !!step.output_dir,
    skip: step.skip === true || index < startFromIndex
  }));
};

// Normalize project JSON key order for consistent display
export const normalizeProjectJson = (json) => {
  if (!json) return json;

  // Normalize step key order
  const normalizeStep = (step) => ({
    id: step.id,
    name: step.name,
    prompt: step.prompt,
    ...(step.skip && { skip: step.skip }),
    ...(step.context_mode && { context_mode: step.context_mode }),
    ...(step.review_mode && { review_mode: step.review_mode }),
    ...(step.datasource && { datasource: step.datasource }),
    ...(step.reference_files && { reference_files: step.reference_files }),
    ...(step.output_dir && { output_dir: step.output_dir }),
    ...(step.delay_ms && { delay_ms: step.delay_ms }),
    ...(step.success_criteria && { success_criteria: step.success_criteria }),
    ...(step.review_notes && { review_notes: step.review_notes })
  });

  return {
    project_name: json.project_name,
    ...(json.description && { description: json.description }),
    ...(json.start_from && { start_from: json.start_from }),
    ...(json.start_from_id && { start_from_id: json.start_from_id }),
    ...(json.defaults && { defaults: json.defaults }),
    steps: (json.steps || []).map(normalizeStep)
  };
};
